import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MoreHorizontal, Shield, ShieldAlert, BadgeCheck, Gift } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SubscriptionDialog } from "./SubscriptionDialog";

import { User } from "./types";

interface UserTableProps {
  users: User[];
  loading: boolean;
  onRefresh?: () => void; // Callback to refresh list after update
}

export function UserTable({ users, loading, onRefresh }: UserTableProps) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);

  const handleAdjustSubscription = (user: User) => {
    setSelectedUser(user);
    setSubscriptionDialogOpen(true);
  };

  // Format duration days for display
  const formatDuration = (days?: number | null) => {
    if (!days) return '-';
    if (days >= 365) return t('admin.users.duration.years', { count: Math.floor(days / 365) });
    if (days >= 30) return t('admin.users.duration.months', { count: Math.floor(days / 30) });
    return t('admin.users.duration.days', { count: days });
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center rounded-xl border-2 border-black bg-white">
        <div className="flex flex-col items-center gap-2">
           <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-black" />
           <span className="font-bold text-gray-500">{t('admin.users.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-hidden rounded-xl border-2 border-black shadow-[4px_4px_0_#000]">
        <div className="overflow-x-auto">
          <table className="w-full bg-white text-left text-sm">
            <thead className="bg-gray-100 text-gray-500">
              <tr className="border-b-2 border-black">
                <th className="px-4 py-4 font-bold uppercase tracking-wider">{t('admin.users.columns.user')}</th>
                <th className="px-4 py-4 font-bold uppercase tracking-wider">{t('admin.users.columns.subscription')}</th>
                <th className="px-4 py-4 font-bold uppercase tracking-wider">{t('admin.users.columns.grantedAt')}</th>
                <th className="px-4 py-4 font-bold uppercase tracking-wider">{t('admin.users.columns.giftDuration')}</th>
                <th className="px-4 py-4 font-bold uppercase tracking-wider">{t('admin.users.columns.expiresAt')}</th>
                <th className="px-4 py-4 font-bold uppercase tracking-wider">{t('admin.users.columns.registeredAt')}</th>
                <th className="px-4 py-4 font-bold uppercase tracking-wider text-right">{t('admin.users.columns.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y-2 divide-gray-100">
              {users.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-gray-500 font-medium">
                    {t('admin.users.noResults')}
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id} className="transition-colors hover:bg-gray-50 group">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-black bg-[#FFD93D] font-bold text-black">
                          {user.avatar_url ? (
                            <img src={user.avatar_url} alt="" className="h-full w-full rounded-full object-cover" />
                          ) : (
                            (user.name || user.email || "?").charAt(0).toUpperCase()
                          )}
                        </div>
                        <div>
                          <div className="font-bold text-gray-900">{user.name || t('admin.users.unnamed')}</div>
                          {!user.name && <div className="text-xs text-gray-500">{user.email}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2 flex-wrap">
                        {user.subscription_status === "gifted" && (
                          <span className="inline-flex items-center gap-1.5 rounded-full border-2 border-black bg-[#E0B0FF] px-3 py-1 text-xs font-bold text-black">
                            <Gift className="h-3.5 w-3.5" />
                            {t('admin.users.status.gifted')}
                          </span>
                        )}
                        
                        {user.subscription_status === "suspended" && (
                          <span className="inline-flex items-center gap-1.5 rounded-full border-2 border-black bg-red-100 px-3 py-1 text-xs font-bold text-red-600">
                            <ShieldAlert className="h-3.5 w-3.5" />
                            {t('admin.users.status.suspended')}
                          </span>
                        )}

                        {user.subscription_status !== "suspended" && (
                          user.subscription_tier === "pro" ? (
                            <span className="inline-flex items-center gap-1.5 rounded-full border-2 border-black bg-[#6FEDD6] px-3 py-1 text-xs font-bold text-black">
                              <BadgeCheck className="h-3.5 w-3.5" />
                              PRO
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-gray-100 px-3 py-1 text-xs font-bold text-gray-500">
                              FREE
                            </span>
                          )
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 font-medium text-gray-600">
                      {user.gift_granted_at ? new Date(user.gift_granted_at).toLocaleDateString(i18n.language) : '-'}
                    </td>
                    <td className="px-4 py-4 font-medium text-gray-600">
                      {user.gift_duration_days ? formatDuration(user.gift_duration_days) : '-'}
                    </td>
                    <td className="px-4 py-4">
                      {user.subscription_end_date ? (
                        <span className="font-bold text-black">{new Date(user.subscription_end_date).toLocaleDateString(i18n.language)}</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4 font-medium text-gray-600">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString(i18n.language) : "-"}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="rounded-lg p-2 hover:bg-gray-200 group-hover:bg-white border border-transparent group-hover:border-gray-200">
                            <MoreHorizontal className="h-5 w-5 text-gray-500" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48 border-2 border-black">
                          <DropdownMenuLabel>{t('admin.users.menu.title')}</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => navigate(`/admin/users/${user.id}`)} className="cursor-pointer font-medium">
                            {t('admin.users.menu.viewDetails')}
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => handleAdjustSubscription(user)}
                            className="cursor-pointer font-medium"
                            >
                            {t('admin.users.menu.adjustSubscription')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination (Simple) */}
        <div className="flex items-center justify-between border-t-2 border-black bg-gray-50 px-6 py-4">
          <span className="text-sm font-bold text-gray-500">{t('admin.users.pagination.showing', { count: users.length })}</span>
          <div className="flex gap-2">
            <button className="rounded-lg border-2 border-black bg-white px-3 py-1.5 text-sm font-bold disabled:opacity-50 hover:bg-gray-100">
              {t('admin.users.pagination.prev')}
            </button>
            <button className="rounded-lg border-2 border-black bg-white px-3 py-1.5 text-sm font-bold hover:bg-gray-100">
              {t('admin.users.pagination.next')}
            </button>
          </div>
        </div>
      </div>

      <SubscriptionDialog 
        user={selectedUser}
        open={subscriptionDialogOpen}
        onOpenChange={setSubscriptionDialogOpen}
        onSuccess={() => {
            if (onRefresh) onRefresh();
        }}
      />
    </>
  );
}
